import React from "react"

import { useDroppable } from "@dnd-kit/core"

import { cn } from "@/utils/cn"

import {
  DASHBOARD_GRID_COLUMNS,
  DASHBOARD_GUTTER_PX,
} from "../../utils/dashboardWidgetSizing"

/** Slot ids carry this prefix so a drop can tell them from a widget id. */
const DROP_SLOT_ID_PREFIX = "dashboard-drop-slot:"

export function isDropSlotId(id: string): boolean {
  return id.startsWith(DROP_SLOT_ID_PREFIX)
}

/** Id of the gap before a card, or after the last one in its row. */
export function dropSlotId(rowIndex: number, slotIndex: number): string {
  return `${DROP_SLOT_ID_PREFIX}row:${rowIndex}:${slotIndex}`
}

/** Id of the boundary a new row can open on, counting rows above it. */
export function rowBoundarySlotId(boundaryIndex: number): string {
  return `${DROP_SLOT_ID_PREFIX}boundary:${boundaryIndex}`
}

/**
 * An in-row slot reaches a gutter's width either side of the gap it sits on, wide
 * enough to aim at, and stops a gutter short of the row's top and bottom edges so
 * that aiming near either one means the boundary there instead.
 */
const SLOT_REACH_PX = DASHBOARD_GUTTER_PX

/**
 * Boundaries reach further into the rows either side than an in-row slot does.
 * A whole row is a coarser thing to aim at than a gap between two cards, and the
 * cards themselves are the fallback, so a band only a gutter deep loses almost
 * every attempt to the card underneath it.
 */
const BOUNDARY_REACH_PX = DASHBOARD_GUTTER_PX * 2

/**
 * Below the last row there is no card to lose to, so the bottom boundary keeps
 * reaching: dragging clear of the dashboard means adding a row at the end.
 */
const BOTTOM_BOUNDARY_REACH_PX = DASHBOARD_GUTTER_PX * 8

/** The line that lights up under the pointer, shared by both kinds of slot. */
const SlotIndicator: React.FC<{ isOver: boolean; vertical: boolean }> = ({
  isOver,
  vertical,
}) => (
  <span
    className={cn(
      "rounded-full transition-opacity",
      vertical ? "h-full w-[3px]" : "h-[3px] w-full",
      isOver ? "bg-coval-border-active opacity-100" : "opacity-0",
    )}
  />
)

interface WidgetDropSlotProps {
  id: string
  /** Row of the CSS grid the slot sits in, counting from the top. */
  rowIndex: number
  /** Column the slot sits on, 0 through DASHBOARD_GRID_COLUMNS. */
  columnOffset: number
}

/**
 * A landing point between two cards, or at either end of a row. Only rendered
 * while a widget is being dragged, so it never intercepts an ordinary click.
 */
export const WidgetDropSlot: React.FC<WidgetDropSlotProps> = ({
  id,
  rowIndex,
  columnOffset,
}) => {
  const { setNodeRef, isOver } = useDroppable({ id })
  const isRowEnd = columnOffset >= DASHBOARD_GRID_COLUMNS

  return (
    <div
      ref={setNodeRef}
      aria-hidden="true"
      data-testid={id}
      className="pointer-events-auto z-20 flex items-center justify-center"
      style={{
        gridRow: rowIndex + 1,
        // A slot at the far edge has no column to its right to anchor to, so
        // it hangs off the end of the last one instead.
        gridColumn: isRowEnd ? DASHBOARD_GRID_COLUMNS : columnOffset + 1,
        justifySelf: isRowEnd ? "end" : "start",
        width: SLOT_REACH_PX * 2,
        marginInline: isRowEnd
          ? `0 ${-SLOT_REACH_PX}px`
          : `${-SLOT_REACH_PX}px 0`,
        marginBlock: SLOT_REACH_PX,
      }}
    >
      <SlotIndicator isOver={isOver} vertical />
    </div>
  )
}

interface RowBoundaryDropSlotProps {
  id: string
  /** Rows above the boundary; `rowCount` puts it below the last row. */
  boundaryIndex: number
  rowCount: number
}

/**
 * A landing point on the line between two rows, for opening a row of its own
 * there. It straddles the boundary without taking height from either row.
 */
export const RowBoundaryDropSlot: React.FC<RowBoundaryDropSlotProps> = ({
  id,
  boundaryIndex,
  rowCount,
}) => {
  const { setNodeRef, isOver } = useDroppable({ id })
  const isBelowLastRow = boundaryIndex >= rowCount

  return (
    <div
      ref={setNodeRef}
      aria-hidden="true"
      data-testid={id}
      className="pointer-events-auto z-20 flex items-center px-1.5"
      style={{
        // Hanging off the bottom of the last row keeps the grid from growing
        // an extra row every time a drag starts.
        gridRow: isBelowLastRow ? rowCount : boundaryIndex + 1,
        gridColumn: `1 / span ${DASHBOARD_GRID_COLUMNS}`,
        alignSelf: isBelowLastRow ? "end" : "start",
        height: isBelowLastRow
          ? BOUNDARY_REACH_PX + BOTTOM_BOUNDARY_REACH_PX
          : BOUNDARY_REACH_PX * 2,
        marginBlock: isBelowLastRow
          ? `0 ${-BOTTOM_BOUNDARY_REACH_PX}px`
          : `${-BOUNDARY_REACH_PX}px 0`,
      }}
    >
      <SlotIndicator isOver={isOver} vertical={false} />
    </div>
  )
}
